/* eslint-disable no-nested-ternary */
import PropTypes from 'prop-types';
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Box, Dialog, DialogContent, IconButton, LinearProgress, Stack, Typography, Button, Radio, Zoom, Tooltip } from '@mui/material'
import { useTheme } from '@mui/material/styles';
import ReactHtmlParser from 'react-html-parser';
import { Iconify } from 'src/components'
import { AISummaryModal } from 'src/components/callScriptV2/components';
import { TAB_HEAD } from 'src/constants/LeadDetailConst';
import { useSelector } from 'react-redux';
import { dispatch } from 'src/redux/store';
import { getActivities } from 'src/redux/slices/activity';
import { updateReminder, followUpSlice, getLeadReminderData, leadTaskListCounter, updateTaskCounter, updateLeadTaskCounter } from 'src/redux/slices/lead';
import { CustomAvatar, AssignUserAvatar } from 'src/components/custom-avatar';
import { GetPracticeAvatar } from 'src/utils/createAvatar';
import { callApi } from 'src/utils/apiCall';
import { reminderFields } from 'src/constants/fields';
import moment from 'moment';
import capitalizeFirstLetter from 'src/utils/capitalizedFunc';
import { selectChoicesData } from 'src/redux/selectors/dataGridSelectors';
import useSnackbar from 'src/hooks/useCustomSnackbar';
import { getMessage } from 'src/utils/getTasksData';
import LeadDetailViewBody from 'src/sections/@dashboard/leadDetail/LeadDetailViewBody';

// Helper function to check if a note is an AI note
const parseAINote = (noteContent) => {
    if (!noteContent) return null;
    try {
        const parsed = JSON.parse(noteContent);
        if (parsed && parsed.AI_NOTES === true) {
            return parsed;
        }
    } catch (e) {
        // Not a JSON note, return null
    }
    return null;
};

const getNoteDisplayText = (noteContent) => {
    const aiData = parseAINote(noteContent);
    if (aiData) {
        return 'AI Call Summary - Click to view';
    }
    return capitalizeFirstLetter(noteContent);
};

// ── Timeline styles ──
const timelineItem = {
    display: 'flex',
    position: 'relative',
    pb: '22px',
    '&:last-child': { pb: 0 },
};

const timelineLine = {
    position: 'absolute',
    left: '13px',
    top: '32px',
    bottom: 0,
    width: '1px',
    bgcolor: 'rgba(145, 158, 171, 0.12)',
};

const timelineDot = (color = '#919EAB') => ({
    width: 28,
    height: 28,
    borderRadius: '50%',
    bgcolor: `${color}14`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    mt: '1px',
});

const timelineContent = {
    flex: 1,
    minWidth: 0,
    ml: '16px',
};

// Typography scale — macOS HIG style (matches Apple Mail / Notes list views).
// Body 13 / caption 11, 3 weights (400/500/600), 3 grays.
const txtBody = { fontSize: '13px', fontWeight: 500, color: '#212B36', lineHeight: 1.45 };
const txtBodyStrong = { fontSize: '13px', fontWeight: 600, color: '#212B36', lineHeight: 1.45 };
const txtBodyMuted = { fontSize: '13px', fontWeight: 400, color: '#637381', lineHeight: 1.45 };
const txtCaption = { fontSize: '11px', fontWeight: 400, color: '#919EAB', lineHeight: 1.4 };
const txtCaptionStrong = { fontSize: '11px', fontWeight: 600, color: '#637381', lineHeight: 1.4 };
const txtSectionLabel = { fontSize: '11px', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#919EAB', lineHeight: 1.4 };

const statusColors = {
    NEW: { bg: 'rgba(0, 184, 217, 0.08)', color: '#006C9C' },
    CONSULTATION_BOOKED: { bg: 'rgba(142, 51, 255, 0.08)', color: '#5119B7' },
    CONSULTATION_ATTENDED: { bg: 'rgba(255, 193, 7, 0.08)', color: '#B78103' },
    FAILED_TO_ATTEND_CONSULTATION: { bg: 'rgba(255, 72, 66, 0.08)', color: '#B71D18' },
    TREATMENT_STARTED: { bg: 'rgba(84, 214, 44, 0.08)', color: '#229A16' },
    CLOSED: { bg: 'rgba(145, 158, 171, 0.08)', color: '#637381' },
};

const getStatusLabel = (status) => {
    const match = TAB_HEAD.find(e => e.value === status);
    return match?.label || status;
};

const getStatusColor = (status) => statusColors[status] || statusColors.CLOSED;

// Parse different date formats to a sortable timestamp
const parseEventDate = (dateStr, format) => {
    if (!dateStr) return 0;
    // Try explicit format first, then ISO, then common formats
    const formats = format ? [format] : [];
    formats.push('DD-MM-YYYY HH:mm:ss.SSSSSSZ', 'DD-MM-YYYY HH:mm:ss', 'DD/MM/YYYY HH:mm:ss', 'YYYY-MM-DDTHH:mm:ss', moment.ISO_8601);
    const m = moment(dateStr, formats, true);
    return m.isValid() ? m.valueOf() : 0;
};

// Format activity timestamp — smart relative for recent, absolute for older.
const formatActivityTime = (timestamp) => {
    const m = moment(timestamp, ['DD-MM-YYYY HH:mm:ss.SSSSSSZ', 'DD-MM-YYYY HH:mm:ss', moment.ISO_8601]);
    if (!m.isValid()) return '';
    const now = moment();
    const diffHours = now.diff(m, 'hours');
    const diffDays = now.diff(m.clone().startOf('day'), 'days');
    if (diffHours < 24 && diffDays === 0) return m.fromNow();
    if (diffDays === 1) return `Yesterday at ${m.format('h:mm a')}`;
    if (diffDays > 1 && diffDays <= 6) return `${m.format('dddd')} at ${m.format('h:mm a')}`;
    if (m.year() === now.year()) return m.format('DD MMM, h:mm a');
    return m.format('DD MMM YYYY, h:mm a');
};

// Date-group bucket label for an event timestamp (in ms).
const getDateGroupLabel = (tsMs) => {
    const m = moment(tsMs);
    if (!m.isValid()) return 'Earlier';
    const today = moment().startOf('day');
    const yesterday = today.clone().subtract(1, 'day');
    const weekStart = today.clone().subtract(6, 'days');
    if (m.isSameOrAfter(today)) return 'Today';
    if (m.isSameOrAfter(yesterday)) return 'Yesterday';
    if (m.isSameOrAfter(weekStart)) return 'This week';
    return 'Earlier';
};

// Outbound contact = something we sent OUT to the lead.
const isOutboundContact = (act) => {
    const v = act?.verb;
    if (v === 'message_sent' || v === 'whatsapp_message_sent') return true;
    if (v === 'note_added') {
        const noteType = act?.action_object?.note;
        return noteType === 'FOLLOW_UP' || noteType === 'AWAITING_RESPONSE';
    }
    return false;
};

// Activity verb → icon + color mapping.
// NOTE: hex literals (not palette tokens) — colours flow through `${color}1F`
// substring interpolations elsewhere; theme tokens won't resolve there.
const verbConfig = {
    created: { icon: 'solar:user-plus-bold', color: '#00A76F' },
    treatment_added: { icon: 'solar:teeth-bold', color: '#078DEE' },
    lead_treatment_status_transitioned: { icon: 'eva:swap-fill', color: '#FFAB00' },
    note_added: { icon: 'solar:pen-bold', color: '#FF5630' },
    message_sent: { icon: 'ic:round-email', color: '#078DEE' },
    whatsapp_message_sent: { icon: 'ic:baseline-whatsapp', color: '#25D366' },
    appointment_booked: { icon: 'solar:calendar-bold', color: '#5119B7' },
    appointment_attended: { icon: 'eva:checkmark-circle-2-fill', color: '#00A76F' },
    appointment_failed: { icon: 'eva:close-circle-fill', color: '#FF5630' },
    default: { icon: 'solar:bell-bold', color: '#919EAB' },
};

const getVerbConfig = (verb) => verbConfig[verb] || verbConfig.default;

// WhatsApp delivery status config
const whatsappStatusConfig = {
    read: { label: 'Read', color: '#078DEE', icon: 'eva:done-all-fill' },
    delivered: { label: 'Delivered', color: '#919EAB', icon: 'eva:done-all-fill' },
    sent: { label: 'Sent', color: '#C4CDD5', icon: 'mdi:check' },
    failed: { label: 'Failed', color: '#FF5630', icon: 'eva:close-fill' },
};

// Should this activity be filtered out?
const shouldFilterActivity = (act) => {
    const verb = act?.verb;
    const msg = act?.data?.data?.message || '';
    const actionObj = act?.action_object;

    // Filter internal notification emails ("New whatsapp message from...")
    if (verb === 'message_sent' && actionObj?.message_category === 'NOTIFICATION') return true;

    // Filter NEW→NEW status transitions (noise from lead creation)
    if (verb === 'lead_treatment_status_transitioned' && msg.includes('NEW to NEW')) return true;

    // Filter duplicate treatment auto-removals
    if (verb === 'duplicate_treatment_auto_removed') return true;

    return false;
};

// Consolidate near-simultaneous creation events
const consolidateCreationEvents = (activities) => {
    if (!activities?.length) return activities;

    // Find 'created' events and determine which IDs to absorb
    const absorbedIds = new Set();
    const createdEvents = activities.filter(a => a.verb === 'created');

    createdEvents.forEach((created) => {
        const createdTime = parseEventDate(created.timestamp, 'DD-MM-YYYY HH:mm:ss.SSSSSSZ');
        activities.forEach((other) => {
            if (other.id === created.id) return;
            const otherTime = parseEventDate(other.timestamp, 'DD-MM-YYYY HH:mm:ss.SSSSSSZ');
            if (Math.abs(createdTime - otherTime) < 5000
                && (other.verb === 'treatment_added' || other.verb === 'lead_treatment_status_transitioned')) {
                absorbedIds.add(other.id);
            }
        });
    });

    // Deduplicate same-second note_added events (keep the one with additional_notes)
    const deduped = activities.filter(a => !absorbedIds.has(a.id));
    const result = [];

    deduped.forEach((act) => {
        if (act.verb === 'note_added') {
            const actTime = act.timestamp?.substring(0, 19);
            const existing = result.find(
                r => r.verb === act.verb && r.timestamp?.substring(0, 19) === actTime
                    && r.action_object?.note === act.action_object?.note
            );
            if (existing) {
                // Keep the one with additional_notes
                if (act.action_object?.additional_notes && !existing.action_object?.additional_notes) {
                    const idx = result.indexOf(existing);
                    result[idx] = act;
                }
                return;
            }
        }
        result.push(act);
    });

    return result;
};


const OverviewTab = ({ treatmentTabData, leadId, handleChange, user, LeadNotes, showDetailModes }) => {
    useTheme();
    const { enqueueSnackbar } = useSnackbar();
    const { practiceTreatment, practiceId } = useSelector((state) => state.practiceTreatmentList);
    const [detailDialog, setDetailDialog] = useState(null); // 'popup' | 'fullscreen' | null
    const { activityList } = useSelector((state) => state.activity);
    const { leadFlowReminder, followLead } = useSelector((state) => state.lead);
    const activeFollowLead = followLead && (followLead.id === leadId) ? followLead : null;
    const choicesData = useSelector(selectChoicesData);

    const [completedTasks, setCompletedTasks] = useState([]);
    const [markComplete, setMarkComplete] = useState(false);
    const [editingPrice, setEditingPrice] = useState(null);
    const [priceValue, setPriceValue] = useState('');
    const [expandedActivity, setExpandedActivity] = useState(null);

    const checklistSummary = useMemo(() => {
        if (!choicesData?.length || !activeFollowLead?.lead_treatments?.length) return null;
        const treatment = activeFollowLead.lead_treatments[0];
        const attrs = treatment?.lead_treatment_attributes || [];
        const meta = activeFollowLead?.lead_metadata || [];
        const items = [];
        choicesData.forEach((choice) => {
            if (
                choice?.metadata?.tables?.includes('Attended Checklists')
                || choice?.metadata?.tables?.includes('Consultation Attended Leads')
            ) {
                items.push({ key: choice.name, label: choice.name });
            }
        });
        if (!items.length) return null;
        const completed = [];
        const pending = [];
        items.forEach((item) => {
            const attr = attrs.find((a) => a.key === item.key);
            let val = attr?.data?.[0]?.value || '';
            if (!val) {
                const m = meta.find((x) => x.key === item.key);
                val = m?.value || '';
            }
            if (val && val.trim() !== '') {
                completed.push({ ...item, value: val, updatedAt: attr?.updated_at || null });
            } else {
                pending.push(item);
            }
        });
        return { items, completed, pending, total: items.length };
    }, [choicesData, activeFollowLead]);

    const [aiSummaryModalOpen, setAISummaryModalOpen] = useState(false);
    const [selectedAIData, setSelectedAIData] = useState(null);

    const handleOpenAISummary = useCallback((aiData) => {
        setSelectedAIData(aiData);
        setAISummaryModalOpen(true);
    }, []);

    const handleCloseAISummary = useCallback(() => {
        setAISummaryModalOpen(false);
        setSelectedAIData(null);
    }, []);

    useEffect(() => {
        if (leadId) {
            dispatch(getActivities(practiceId, leadId, 20, 0));
        }
    }, [leadId, practiceId]);

    // Fetch completed tasks
    useEffect(() => {
        if (practiceId && leadId) {
            const fetchCompleted = async () => {
                const fields = `&fields=${reminderFields}`;
                const res = await callApi(`/api/v1/practices/${practiceId}/reminders/?group__in=TODO,FOLLOW_UP,CALLBACK_REQUEST&is_completed=true&lead=${leadId}&ordering=-datetime&limit=10&offset=0${fields}&lead__is_archived=false`);
                if (res?.status === 200) {
                    setCompletedTasks(res.data?.results || []);
                }
            };
            fetchCompleted();
        }
    }, [practiceId, leadId]);

    // Task mark complete handler
    const handleReminder = (value) => {
        dispatch(updateTaskCounter([]));
        if (value === 'markComplete' && user) {
            dispatch(updateLeadTaskCounter([]));
            dispatch(getLeadReminderData(practiceId, 'LeadDetail', 10, 0, 'NEW', user?.lead_id || '', '', '', 'leadNotes', 'counter', 'All', undefined, true));
            dispatch(leadTaskListCounter(practiceId, 'LeadDetail', user?.status, user?.lead_id, '', '', undefined, true));
            dispatch(followUpSlice(user?.lead_id, practiceId, 'LeadDetail'));
        }
    };

    const markCompleteBtn = (value) => {
        setMarkComplete(value);
        dispatch(updateReminder({ is_completed: true }, value, practiceId, handleReminder, 'markComplete', (data) => {
            setMarkComplete(false);
            enqueueSnackbar(data.message || '', {
                variant: data.success ? 'success' : 'error',
                anchorOrigin: { vertical: 'top', horizontal: 'center' },
            });
        }));
    };

    // ── Build unified event stream ──
    const eventStream = useMemo(() => {
        const events = [];

        // Activities — filter noise, consolidate creation events
        if (Array.isArray(activityList?.results)) {
            const filtered = activityList.results.filter(act => !shouldFilterActivity(act));
            const consolidated = consolidateCreationEvents(filtered);
            consolidated.forEach((act) => {
                events.push({
                    type: 'activity',
                    date: parseEventDate(act.timestamp, 'DD-MM-YYYY HH:mm:ss.SSSSSSZ'),
                    data: act,
                });
            });
        }

        // Pending tasks
        if (Array.isArray(leadFlowReminder?.results)) {
            leadFlowReminder.results.forEach((task) => {
                events.push({
                    type: 'task',
                    isCompleted: false,
                    date: parseEventDate(task.datetime),
                    data: task,
                });
            });
        }

        // Completed tasks
        if (Array.isArray(completedTasks)) {
            completedTasks.forEach((task) => {
                events.push({
                    type: 'task',
                    isCompleted: true,
                    date: parseEventDate(task.datetime),
                    data: task,
                });
            });
        }

        // Notes — skip notes already represented as activity events
        if (Array.isArray(LeadNotes)) {
            // Collect note IDs from activity action_objects to avoid duplicates
            const activityNoteIds = new Set();
            if (Array.isArray(activityList?.results)) {
                activityList.results.forEach((act) => {
                    if (act.verb === 'note_added' && act.action_object?.id) {
                        activityNoteIds.add(act.action_object.id);
                    }
                });
            }
            LeadNotes.forEach((note) => {
                // Skip notes already shown as activities
                if (note?.id && activityNoteIds.has(note.id)) return;
                events.push({
                    type: 'note',
                    date: parseEventDate(note?.updated_at),
                    data: note,
                });
            });
        }

        // Sort newest first
        events.sort((a, b) => b.date - a.date);
        return events;
    }, [activityList, leadFlowReminder, completedTasks, LeadNotes]);

    // Most recent outbound contact id — marks one row with a subtle ring
    // so the user can spot "where I left off" without scrolling.
    const latestOutboundId = useMemo(() => {
        const items = activityList?.results || [];
        const found = items.find((it) => isOutboundContact(it));
        return found ? (found.id || null) : null;
    }, [activityList?.results]);

    // ── Render helpers ──
    const getActivityLabel = (act) => {
        const verb = act?.verb;
        const msg = act?.data?.data?.message || '';
        const actionObj = act?.action_object;

        switch (verb) {
            case 'created':
                return 'Lead created';
            case 'treatment_added':
                return `${capitalizeFirstLetter(msg.replace(' treatment added.', ''))} added`;
            case 'lead_treatment_status_transitioned': {
                // Extract status names from message like "from X to Y"
                const match = msg.match(/from\s+(\S+)\s+to\s+(\S+)/i);
                if (match) {
                    return `Status: ${getStatusLabel(match[1]?.trim())} → ${getStatusLabel(match[2]?.trim())}`;
                }
                return capitalizeFirstLetter(msg);
            }
            case 'note_added': {
                if (actionObj?.note === 'UNABLE_TO_CONTACT') {
                    const via = actionObj?.contacted_via ? ` via ${capitalizeFirstLetter(actionObj.contacted_via.toLowerCase())}` : '';
                    const extra = actionObj?.additional_notes ? ` — ${actionObj.additional_notes}` : '';
                    return `Unable to contact${via}${extra}`;
                }
                if (actionObj?.note === 'FOLLOW_UP') {
                    const extra = actionObj?.additional_notes ? ` — ${actionObj.additional_notes}` : '';
                    return `Follow up${extra}`;
                }
                if (actionObj?.note === 'AWAITING_RESPONSE') return 'Waiting/Scheduled';
                if (msg === 'Awaiting Response.') return 'Waiting/Scheduled';
                return capitalizeFirstLetter(msg);
            }
            case 'message_sent': {
                const subject = actionObj?.subject || '';
                const isAuto = actionObj?.message_group === 'SYSTEM' || actionObj?.message_category === 'SEQUENCE_MESSAGE';
                const prefix = isAuto ? 'Auto email' : 'Email sent';
                if (subject && !subject.startsWith('$')) {
                    return `${prefix}: ${subject}`;
                }
                return prefix;
            }
            case 'whatsapp_message_sent': {
                // Extract first ~80 chars of the actual message
                const rawMsg = msg.replace('Whatsapp message sent - ', '');
                const truncated = rawMsg.length > 80 ? `${rawMsg.substring(0, 80)}...` : rawMsg;
                return truncated;
            }
            default:
                return capitalizeFirstLetter(msg);
        }
    };

    const getActivityVerbIcon = (act) => {
        const verb = act?.verb;
        // Override icon for note_added based on sub-type
        if (verb === 'note_added') {
            const noteType = act?.action_object?.note;
            if (noteType === 'UNABLE_TO_CONTACT') return { icon: 'solar:phone-calling-bold', color: '#FF5630' };
            if (noteType === 'FOLLOW_UP') return { icon: 'mdi:clock-time-three-outline', color: '#FFAB00' };
            if (noteType === 'AWAITING_RESPONSE') return { icon: 'mdi:clock-time-three-outline', color: '#078DEE' };
            return { icon: 'solar:pen-bold', color: '#637381' };
        }
        // Auto emails get a different shade
        if (verb === 'message_sent') {
            const isAuto = act?.action_object?.message_group === 'SYSTEM' || act?.action_object?.message_category === 'SEQUENCE_MESSAGE';
            if (isAuto) return { icon: 'mdi:email-fast-outline', color: '#919EAB' };
            return { icon: 'ic:round-email', color: '#078DEE' };
        }
        const config = getVerbConfig(verb);
        return { icon: config.icon, color: config.color };
    };

    // Get delivery status config for messages (WhatsApp or Email)
    const getMessageStatusConfig = (act) => {
        const verb = act?.verb;
        const actionObj = act?.action_object;

        // WhatsApp delivery status
        if (verb === 'whatsapp_message_sent') {
            const status = actionObj?.status || 'sent';
            return whatsappStatusConfig[status] || whatsappStatusConfig.sent;
        }

        // Email delivery status — check sent_messages event_counts for delivery tracking
        if (verb === 'message_sent' && actionObj?.type === 'EMAIL') {
            if (actionObj?.message_category === 'NOTIFICATION') return null;

            // Check sent_messages event_counts for delivery tracking
            const eventCounts = actionObj?.sent_messages?.flatMap(sm => sm?.event_counts || []) || [];
            const hasEvent = (eventName) => eventCounts.some(ec => ec?.event === eventName && ec?.count > 0);

            if (hasEvent('clicked')) return { label: 'Clicked', color: '#00A76F', icon: 'mdi:cursor-default-click' };
            if (hasEvent('opened')) return { label: 'Opened', color: '#078DEE', icon: 'mdi:email-open' };
            if (hasEvent('delivered')) return { label: 'Delivered', color: '#919EAB', icon: 'eva:done-all-fill' };

            // Fallback
            const isAuto = actionObj?.message_group === 'SYSTEM' || actionObj?.message_category === 'SEQUENCE_MESSAGE';
            if (isAuto) return { label: 'Auto', color: '#C4CDD5', icon: 'mdi:robot-outline' };
            return { label: 'Sent', color: '#919EAB', icon: 'mdi:check' };
        }

        return null;
    };

    const renderActivity = (act, isLast, isLatest) => {
        const verb = act?.verb;
        const { icon, color } = getActivityVerbIcon(act);
        const actorName = act?.actor?.first_name && act?.actor?.first_name.trim() ? `${act.actor.first_name} ${act.actor.last_name || ''}`.trim() : '';
        const timeStr = formatActivityTime(act.timestamp);
        const isWhatsApp = verb === 'whatsapp_message_sent';
        const statusConfig = getMessageStatusConfig(act);

        return (
            <Box key={`activity-${act.id}`} sx={timelineItem}>
                {!isLast && <Box sx={timelineLine} />}
                <Box sx={{ position: 'relative', flexShrink: 0, alignSelf: 'flex-start', width: 28, height: 28, mt: '1px' }}>
                    {isLatest && (
                        <Tooltip title="Most recent contact" placement="top">
                            <Box sx={{
                                position: 'absolute',
                                inset: -2,
                                borderRadius: '50%',
                                border: `1.5px solid ${color}`,
                                opacity: 0.55,
                                pointerEvents: 'none',
                            }} />
                        </Tooltip>
                    )}
                    <Box sx={{ ...timelineDot(color), mt: 0 }}>
                        <Iconify icon={icon} width={14} height={14} sx={{ color }} />
                    </Box>
                </Box>
                <Box sx={timelineContent}>
                    {(() => {
                        const isExpandable = isWhatsApp || verb === 'message_sent';
                        const isExpanded = expandedActivity === act.id;
                        const toggle = () => setExpandedActivity(isExpanded ? null : act.id);
                        return (
                            <Typography
                                component={isExpandable ? 'div' : 'p'}
                                role={isExpandable ? 'button' : undefined}
                                tabIndex={isExpandable ? 0 : undefined}
                                aria-expanded={isExpandable ? isExpanded : undefined}
                                sx={{
                                    ...txtBody,
                                    background: 'transparent',
                                    border: 'none',
                                    padding: 0,
                                    textAlign: 'left',
                                    display: 'block',
                                    width: '100%',
                                    outline: 'none',
                                    margin: 0,
                                    ...(isExpandable && {
                                        whiteSpace: isExpanded ? 'normal' : 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        cursor: 'pointer',
                                        '&:focus-visible': {
                                            boxShadow: '0 0 0 2px rgba(24,144,255,0.25)',
                                            borderRadius: '4px',
                                        },
                                    }),
                                }}
                                onClick={isExpandable ? toggle : undefined}
                                onKeyDown={isExpandable ? (e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        toggle();
                                    }
                                } : undefined}
                            >
                                {getActivityLabel(act)}
                            </Typography>
                        );
                    })()}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: '4px' }}>
                        {actorName && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <CustomAvatar
                                    src={act?.actor?.avatar || GetPracticeAvatar(practiceTreatment, practiceId)[0]?.logo}
                                    name={act?.actor?.first_name}
                                    color="default"
                                    sx={{ width: 16, height: 16, fontSize: '0.55rem' }}
                                />
                                <Typography sx={txtCaption}>{actorName}</Typography>
                            </Box>
                        )}
                        {timeStr && <Typography sx={txtCaption}>{actorName ? '·' : ''} {timeStr}</Typography>}
                        {statusConfig && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px', ml: 'auto' }}>
                                <Iconify icon={statusConfig.icon} width={12} height={12} sx={{ color: statusConfig.color }} />
                                <Typography sx={{ ...txtCaption, fontWeight: 600, color: statusConfig.color }}>{statusConfig.label}</Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
        );
    };

    const getTaskTimeColor = (task, completed) => {
        if (completed) return '#919EAB';
        if (task?.datetime_human?.includes('now' || 'NOW')) return '#00A76F';
        return '#FF5630';
    };

    const renderTask = (task, completed, isLast) => {
        const assigneeData = task?.assignees?.filter((el) => el?.user?.first_name && !el?.user?.is_superuser);
        return (
            <Box key={`task-${task.id}`} sx={{ ...timelineItem, opacity: completed ? 0.55 : 1 }}>
                {!isLast && <Box sx={timelineLine} />}
                {completed ? (
                    <Box sx={timelineDot('#00A76F')}>
                        <Iconify icon="eva:checkmark-circle-2-fill" width={14} height={14} sx={{ color: '#00A76F' }} />
                    </Box>
                ) : (
                    <Box sx={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: '1px' }}>
                        <Tooltip title={task.id === markComplete ? 'Mark as incomplete' : 'Mark as complete'} placement='bottom' TransitionComponent={Zoom} TransitionProps={{ timeout: 300 }}>
                            <Radio
                                checked={task.id === markComplete}
                                onClick={() => markCompleteBtn(task.id)}
                                name='radioButtons'
                                sx={{ p: 0, 'svg': { width: '18px', height: '18px' } }}
                            />
                        </Tooltip>
                    </Box>
                )}
                <Box sx={timelineContent}>
                    <Typography sx={{ ...(completed ? { ...txtBody, color: '#919EAB', textDecoration: 'line-through' } : txtBodyStrong) }} noWrap>
                        {task.title !== '' ? capitalizeFirstLetter(task?.title) : getMessage(task?.lead_treatment?.status, task?.lead?.first_name, task?.lead?.last_name, task?.lead_treatment?.treatment, task?.group, task?.method, task?.note)}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: '4px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', ...txtCaption, fontWeight: 600, color: getTaskTimeColor(task, completed) }}>
                            <Iconify icon={'mdi:clock-time-three-outline'} width={12} height={12} sx={{ mr: '3px' }} />
                            {task.datetime_human || ''}
                        </Box>
                        {assigneeData?.length > 0 && (
                            <Box sx={{ display: 'flex', ml: 'auto' }}>
                                {assigneeData.slice(0, 2).map((assignee, index) => (
                                    <AssignUserAvatar
                                        key={index}
                                        sx={{ height: '18px', width: '18px', fontSize: '0.6rem', border: '1px solid #fff', '&:nth-child(2)': { ml: '-4px' } }}
                                        src={assignee.user?.avatar || ''}
                                        name={`${assignee.user?.first_name || ''} ${assignee.user?.last_name || ''}`}
                                        role={assignee.role}
                                    />
                                ))}
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
        );
    };

    const renderNote = (note, isLast) => {
        const aiNoteData = parseAINote(note?.additional_notes);
        const noteText = note?.additional_notes || note?.note || note?.lead_contact?.note || '';
        return (
            <Box key={`note-${note.id || note?.updated_at}`} sx={timelineItem}>
                {!isLast && <Box sx={timelineLine} />}
                <Box sx={timelineDot('#7635dc')}>
                    <Iconify icon="solar:document-text-bold" width={14} height={14} sx={{ color: '#7635dc' }} />
                </Box>
                <Box sx={timelineContent}>
                    {aiNoteData ? (
                        <Button
                            variant="text"
                            startIcon={<Iconify icon="mdi:robot-outline" width={14} />}
                            onClick={() => handleOpenAISummary(aiNoteData)}
                            sx={{
                                ...txtBodyStrong, color: '#7635dc', textTransform: 'none',
                                p: 0, minWidth: 'auto',
                                '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
                            }}
                        >
                            View AI Call Summary
                        </Button>
                    ) : (
                        <Typography sx={txtBodyMuted} noWrap>
                            {getNoteDisplayText(noteText)}
                        </Typography>
                    )}
                    <Typography sx={{ ...txtCaption, mt: '4px' }}>
                        {formatActivityTime(note?.updated_at)}
                    </Typography>
                </Box>
            </Box>
        );
    };

    const hasChecklist = activeFollowLead?.lead_treatments?.some(t => ['CONSULTATION_ATTENDED', 'TREATMENT_STARTED', 'TREATMENT_STARTED_INVISALIGN', 'CLOSED'].includes(t.status)) && checklistSummary && checklistSummary.total > 0;
    const hasEvents = eventStream.length > 0;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Header */}
            <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                p: '10px 20px', borderBottom: '1px solid rgba(145, 158, 171, 0.08)',
            }}>
                <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#212B36' }}>Overview</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {showDetailModes && (
                        <>
                            <Tooltip title="Open in popup" TransitionComponent={Zoom} TransitionProps={{ timeout: 200 }}>
                                <IconButton
                                    size="small"
                                    onClick={() => setDetailDialog('popup')}
                                    sx={{ color: '#919EAB', '&:hover': { color: '#637381', bgcolor: 'rgba(145,158,171,0.08)' } }}
                                >
                                    <Iconify icon="mdi:rectangle-outline" width={18} height={18} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Open in full screen" TransitionComponent={Zoom} TransitionProps={{ timeout: 200 }}>
                                <IconButton
                                    size="small"
                                    onClick={() => setDetailDialog('fullscreen')}
                                    sx={{ color: '#919EAB', '&:hover': { color: '#637381', bgcolor: 'rgba(145,158,171,0.08)' } }}
                                >
                                    <Iconify icon="mdi:fullscreen" width={18} height={18} />
                                </IconButton>
                            </Tooltip>
                        </>
                    )}
                    <Iconify
                        icon={'eva:close-fill'}
                        onClick={() => handleChange('', '')}
                        sx={{ color: '#919EAB', cursor: 'pointer', width: 20, height: 20, '&:hover': { color: '#637381' } }}
                    />
                </Box>
            </Box>

            {/* Scrollable timeline content */}
            <Box sx={{ flex: 1, overflow: 'auto', p: '16px 20px 16px 16px' }}>
                <Box sx={{ position: 'relative' }}>

                    {/* ── Treatment (context — always at top) ── */}
                    {treatmentTabData && treatmentTabData.length > 0 && treatmentTabData.map((item, index) => {
                        const sc = getStatusColor(item.status);
                        const isLastTreatment = index === treatmentTabData.length - 1;
                        const hasMore = hasChecklist || hasEvents;
                        return (
                            <Box key={`treatment-${index}`} sx={timelineItem}>
                                {(!isLastTreatment || hasMore) && <Box sx={timelineLine} />}
                                <Box sx={timelineDot(sc.color)}>
                                    <Iconify icon="solar:teeth-bold" width={14} height={14} sx={{ color: sc.color }} />
                                </Box>
                                <Box sx={timelineContent}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography sx={{ ...txtBodyStrong, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                                            {item?.treatment_data?.treatment_item?.name}
                                        </Typography>
                                        {editingPrice === index ? (
                                            <input
                                                type="text"
                                                value={priceValue}
                                                onChange={(e) => setPriceValue(e.target.value)}
                                                onKeyDown={async (e) => {
                                                    if (e.key === 'Enter') {
                                                        e.target.blur();
                                                    } else if (e.key === 'Escape') {
                                                        e.stopPropagation();
                                                        setEditingPrice(null);
                                                        setPriceValue('');
                                                    }
                                                }}
                                                onBlur={async () => {
                                                    const newPrice = priceValue.trim();
                                                    if (newPrice && newPrice !== String(item?.treatment_data?.price)) {
                                                        const res = await callApi(`/api/v1/practices/${practiceId}/treatments/${item?.treatment_data?.id}/`, 'PATCH', { price: newPrice });
                                                        if (res?.status === 200 && item?.treatment_data) {
                                                            item.treatment_data.price = newPrice;
                                                        }
                                                    }
                                                    setEditingPrice(null);
                                                    setPriceValue('');
                                                }}
                                                autoFocus // eslint-disable-line jsx-a11y/no-autofocus
                                                style={{
                                                    width: '60px', fontSize: '12px', fontWeight: 600, color: '#919EAB',
                                                    border: 'none', borderBottom: '1px solid #919EAB', outline: 'none',
                                                    background: 'transparent', textAlign: 'right', padding: 0,
                                                }}
                                            />
                                        ) : (
                                            <Typography
                                                sx={{
                                                    ...txtCaptionStrong, color: '#919EAB', flexShrink: 0,
                                                    cursor: 'pointer', borderBottom: '1px dashed transparent',
                                                    display: 'flex', alignItems: 'center', gap: '3px',
                                                    '&:hover': { borderBottomColor: '#919EAB' },
                                                    '&:hover .price-edit-icon': { opacity: 0.4 },
                                                }}
                                                onClick={() => { setEditingPrice(index); setPriceValue(String(item?.treatment_data?.price || '')); }}
                                            >
                                                &#163;{item?.treatment_data?.price}
                                                <Iconify className="price-edit-icon" icon="solar:pen-bold" width={10} height={10} sx={{ opacity: 0, transition: 'opacity 0.15s' }} />
                                            </Typography>
                                        )}
                                    </Box>
                                    {item?.treatment_data?.offers?.filter(o => !o.is_expired)?.map((offer, oi) => (
                                        <Box key={oi} sx={{ mt: 0.75, display: 'flex', alignItems: 'baseline', gap: 0.5, '&:hover .offer-edit-icon': { opacity: 0.3 } }}>
                                            <Iconify icon="bxs:offer" width={12} height={12} sx={{ color: '#00A76F', flexShrink: 0, mt: '1px' }} />
                                            <Typography component="span" sx={{ ...txtCaption, color: '#637381', flex: 1 }}>
                                                <Typography component="span" sx={{ ...txtCaptionStrong, color: '#00A76F' }}>
                                                    {capitalizeFirstLetter(offer?.name)}
                                                </Typography>
                                                {offer?.offer && <Box component="span" sx={{ ml: 0.5, '& p, & h1, & h2, & h3, & h4, & h5, & h6, & blockquote, & span, & strong, & em, & li, & ul, & ol': { margin: 0, padding: 0, fontSize: '11px !important', color: '#637381', lineHeight: '1.4 !important', display: 'inline' } }}>{ReactHtmlParser(offer.offer)}</Box>}
                                            </Typography>
                                            {/* TODO: Navigate to treatment settings for offer editing */}
                                            <Iconify className="offer-edit-icon" icon="solar:pen-bold" width={10} height={10} sx={{ opacity: 0, transition: 'opacity 0.15s', flexShrink: 0, cursor: 'pointer' }} />
                                        </Box>
                                    ))}
                                    {item?.treatment_data?.additional_infos?.filter(ai => ai?.categories?.[0] === "FLOW")?.map((info, ii) => (
                                        <Box key={ii} sx={{ mt: 1, '& p, & h1, & h2, & h3, & h4, & h5, & h6, & blockquote': { margin: 0, padding: 0, fontSize: '11px', color: '#637381', lineHeight: 1.4 } }}>{ReactHtmlParser(info?.text)}</Box>
                                    ))}
                                </Box>
                            </Box>
                        );
                    })}

                    {/* ── Checklist (context — stays pinned after treatment) ── */}
                    {hasChecklist && (
                        <Box sx={timelineItem}>
                            {hasEvents && <Box sx={timelineLine} />}
                            <Box sx={timelineDot(checklistSummary.completed.length === checklistSummary.total ? '#00A76F' : '#FFAB00')}>
                                <Iconify icon="eva:checkmark-square-2-fill" width={14} height={14} sx={{ color: checklistSummary.completed.length === checklistSummary.total ? '#00A76F' : '#FFAB00' }} />
                            </Box>
                            <Box sx={timelineContent}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Typography sx={txtBodyStrong}>
                                        Checklist
                                    </Typography>
                                    <Typography sx={{
                                        ...txtCaptionStrong,
                                        color: checklistSummary.completed.length === checklistSummary.total ? '#00A76F' : '#212B36',
                                    }}>
                                        {checklistSummary.completed.length}/{checklistSummary.total}
                                    </Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={Math.round((checklistSummary.completed.length / checklistSummary.total) * 100)}
                                    sx={{
                                        height: 3, borderRadius: 2, bgcolor: 'rgba(145, 158, 171, 0.12)',
                                        '& .MuiLinearProgress-bar': {
                                            borderRadius: 2,
                                            bgcolor: checklistSummary.completed.length === checklistSummary.total ? '#00A76F' : 'primary.main',
                                        },
                                    }}
                                />
                                <Stack spacing={0.25} sx={{ mt: 1 }}>
                                    {checklistSummary.completed.map((item) => {
                                        const lower = item.value?.toLowerCase();
                                        const isYes = lower === 'yes';
                                        const isNo = lower === 'no';
                                        return (
                                            <Box key={item.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.25 }}>
                                                <Iconify
                                                    icon={isYes ? 'eva:checkmark-circle-2-fill' : isNo ? 'eva:close-circle-fill' : 'eva:checkmark-circle-2-fill'}
                                                    sx={{ color: isYes ? '#00A76F' : isNo ? '#FF5630' : '#FFAB00', width: 14, height: 14, flexShrink: 0 }}
                                                />
                                                <Typography sx={{ ...txtCaptionStrong, color: '#212B36', flex: 1 }} noWrap>{item.label}</Typography>
                                            </Box>
                                        );
                                    })}
                                    {checklistSummary.pending.map((item) => (
                                        <Box key={item.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.25 }}>
                                            <Box sx={{ width: 14, height: 14, borderRadius: '50%', border: '1.5px solid #C4CDD5', flexShrink: 0 }} />
                                            <Typography sx={{ ...txtCaption, flex: 1 }} noWrap>{item.label}</Typography>
                                        </Box>
                                    ))}
                                </Stack>
                            </Box>
                        </Box>
                    )}

                    {/* ── Unified event stream — grouped by date (Today / Yesterday / This week / Earlier) ── */}
                    {(() => {
                        const out = [];
                        let lastGroup = null;
                        eventStream.forEach((event, idx) => {
                            const isLast = idx === eventStream.length - 1;
                            const group = getDateGroupLabel(event.date);
                            if (group !== lastGroup) {
                                out.push(
                                    <Box
                                        key={`group-${group}-${idx}`}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            mt: lastGroup === null ? 0 : 1.5,
                                            mb: 1,
                                        }}
                                    >
                                        <Typography sx={{ ...txtSectionLabel, whiteSpace: 'nowrap' }}>
                                            {group}
                                        </Typography>
                                        <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(145,158,171,0.16)' }} />
                                    </Box>
                                );
                                lastGroup = group;
                            }
                            if (event.type === 'activity') {
                                const isLatest = event.data?.id && event.data.id === latestOutboundId;
                                out.push(renderActivity(event.data, isLast, isLatest));
                            } else if (event.type === 'task') {
                                out.push(renderTask(event.data, event.isCompleted, isLast));
                            } else if (event.type === 'note') {
                                out.push(renderNote(event.data, isLast));
                            }
                        });
                        return out;
                    })()}

                </Box>
            </Box>

            {/* AI Summary Modal */}
            {aiSummaryModalOpen && selectedAIData && (
                <AISummaryModal
                    open={aiSummaryModalOpen}
                    onClose={handleCloseAISummary}
                    onCloseAll={handleCloseAISummary}
                    onBackToScript={handleCloseAISummary}
                    data={{
                        classification: selectedAIData.classification?.status || 'Unknown',
                        classificationEmoji: selectedAIData.classification?.emoji || '',
                        readinessScore: selectedAIData.classification?.readinessScore || 0,
                        budget: selectedAIData.classification?.budget || '',
                        availability: selectedAIData.classification?.availability || '',
                        intent: selectedAIData.classification?.intent || '',
                        summary: selectedAIData.summary?.aiSummaryText || '',
                        keyQuote: selectedAIData.summary?.keyQuote || '',
                        sentiment: selectedAIData.summary?.sentiment || 'Neutral',
                        highlights: selectedAIData.summary?.highlights || [],
                        concerns: selectedAIData.concerns || [],
                        tags: selectedAIData.tags || [],
                        bookAppointment: selectedAIData.recommendedActions?.bookAppointment || { recommended: false },
                        sendWhatsApp: selectedAIData.recommendedActions?.sendWhatsApp || { recommended: false },
                        sendEmail: selectedAIData.recommendedActions?.sendEmail || { recommended: false },
                        addReminder: selectedAIData.recommendedActions?.addReminder || { recommended: false },
                        nextSteps: selectedAIData.recommendedActions?.nextSteps || [],
                    }}
                    leadName={selectedAIData.leadInfo?.name || ''}
                    leadInitials={selectedAIData.leadInfo?.initials || ''}
                    callType={selectedAIData.callInfo?.callType || 'Outbound'}
                    callStartTime={selectedAIData.callInfo?.callStartTime || ''}
                    callDuration={selectedAIData.callInfo?.callDuration || ''}
                    dataCompleteness={selectedAIData.callInfo?.dataCompleteness || 0}
                    viewMode
                    savedAIData={selectedAIData}
                />
            )}

            {/* Lead Detail dialogs — triggered by the mode icons in the header */}
            {showDetailModes && detailDialog && (
                <Dialog
                    open
                    fullScreen={detailDialog === 'fullscreen'}
                    maxWidth={detailDialog === 'popup' ? 'lg' : false}
                    fullWidth={detailDialog === 'popup'}
                    onClose={() => setDetailDialog(null)}
                    data-testid="lead-detail-dialog"
                >
                    <DialogContent sx={{ p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <LeadDetailViewBody
                            leadId={leadId}
                            practiceId={practiceId}
                            embedded
                            onNotFound={() => setDetailDialog(null)}
                        />
                    </DialogContent>
                </Dialog>
            )}
        </Box>
    )
}

OverviewTab.propTypes = {
    treatmentTabData: PropTypes.array,
    leadId: PropTypes.any,
    handleChange: PropTypes.func,
    user: PropTypes.any,
    LeadNotes: PropTypes.array,
    showDetailModes: PropTypes.bool,
};

OverviewTab.defaultProps = {
    showDetailModes: false,
};


export default OverviewTab
